import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useCurrentCompany} from "@mytour/platform";
import {
  ACCEPTED_DOCUMENT_MIME_TYPES,
  type AcceptedDocumentMimeType,
  type DocumentParentType,
  type FileDocumentResponse,
  type PrepareDocumentUploadPayload,
} from "@mytour/shared";
import {useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {toast} from "react-toastify";
import {Button, Spinner} from "reactstrap";
import {DocumentCardChrome} from "../file-render/DocumentCardChrome";
import {DocumentTile} from "../file-render/DocumentTile";
import {useCreateTextDocument, useUploadFileDocuments} from "../query/documentQueries";
import {
  DOCUMENT_UPLOAD_CONCURRENCY,
  type DocumentUploadState,
  type UploadFileDocumentItem,
} from "../query/documentUploadQueue";
import {formatBytes, getFileTypeBadge, isImageMime} from "./documentDisplay";
import {validateDocumentFile} from "./documentFileValidation";
import {
  type DocumentUploadScope,
  completeDocumentUploadManifest,
  documentFileChecksum,
  documentUploadManifestKey,
  documentUploadPayload,
  readDocumentUploadManifest,
  saveDocumentUploadManifest,
} from "./documentUploadManifest";

type ComposerState = "idle" | "file" | "note";

type PendingFile = {
  uploadId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  file?: File;
  payload?: PrepareDocumentUploadPayload;
  status?: DocumentUploadState;
  error?: string;
};

type Props = {
  parentType: DocumentParentType;
  parentId: string;
  onActiveChange?: (active: boolean) => void;
  footerSlot?: HTMLElement | null;
  resumeDocument?: FileDocumentResponse;
};

export function DocumentComposer({
  parentType,
  parentId,
  onActiveChange,
  footerSlot,
  resumeDocument,
}: Props) {
  const [state, setState] = useState<ComposerState>("idle");
  const [noteText, setNoteText] = useState("");
  const [isCheckingRecovery, setIsCheckingRecovery] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recoveryInputRef = useRef<HTMLInputElement>(null);
  const recoveryUploadId = useRef<string>();
  const loadedScope = useRef<string>();
  const currentScopeKey = useRef<string>();
  const pendingIntentIds = useRef(new Set<string>());
  const {company} = useCurrentCompany();
  const scope = useMemo<DocumentUploadScope | undefined>(
    () => (company?.id ? {companyId: company.id, parentType, parentId} : undefined),
    [company?.id, parentType, parentId]
  );
  const scopeKey = scope ? documentUploadManifestKey(scope) : undefined;
  currentScopeKey.current = scopeKey;

  useEffect(() => {
    onActiveChange?.(state !== "idle");
  }, [state, onActiveChange]);

  const {createTextDocument, isCreatingTextDocument} = useCreateTextDocument();
  const {uploadFileDocuments, isUploadingFileDocuments} =
    useUploadFileDocuments(handleUploadState);

  useEffect(() => {
    if (!scope || !scopeKey || loadedScope.current === scopeKey) {
      return;
    }
    loadedScope.current = scopeKey;
    pendingIntentIds.current.clear();
    setPendingFiles([]);
    setState("idle");
    try {
      const payloads = readDocumentUploadManifest(scope);
      if (payloads.length > 0) {
        setPendingFiles(payloads.map(pendingFromPayload));
        setState("file");
        void handleUploadItems(
          payloads.map((payload) => ({payload})),
          false
        );
      }
    } catch {
      toast.error("Impossible de lire les envois à reprendre dans ce navigateur");
    }
  }, [scope, scopeKey]);

  useEffect(() => {
    if (
      !resumeDocument ||
      resumeDocument.companyId !== company?.id ||
      resumeDocument.parentId !== parentId ||
      resumeDocument.parentType !== parentType
    ) {
      return;
    }
    const payload = documentUploadPayload(resumeDocument);
    if (!payload) {
      toast.error("Impossible de reprendre cet envoi");
      return;
    }
    setPendingFiles((current) =>
      current.some((pending) => pending.uploadId === payload.uploadId)
        ? current
        : [...current, pendingFromPayload(payload)]
    );
    setState("file");
    void handleUploadItems([{payload}], false);
  }, [resumeDocument, company?.id, parentId, parentType]);

  const isHashing = pendingFiles.some((pending) => !pending.error && !pending.payload);
  const isBusy =
    isCreatingTextDocument || isUploadingFileDocuments || isHashing || isCheckingRecovery;
  const validFiles = pendingFiles.filter((pending) => !pending.error && pending.payload);

  const fileSlotActive = state === "file";
  const noteSlotActive = state === "note";

  return (
    <>
      <div
        style={{
          minHeight: state === "idle" ? 130 : "65vh",
          display: "flex",
          gap: state === "idle" ? 16 : 0,
          alignItems: "stretch",
          transition: "min-height 180ms ease-out, gap 180ms ease-out",
        }}
        data-testid={state === "idle" ? "document-composer-idle" : undefined}
      >
        <ComposerSlot active={fileSlotActive} collapsed={noteSlotActive}>
          {fileSlotActive ? renderFileMode() : renderFileTrigger()}
        </ComposerSlot>
        <ComposerSlot active={noteSlotActive} collapsed={fileSlotActive}>
          {noteSlotActive ? renderNoteMode() : renderNoteTrigger()}
        </ComposerSlot>
      </div>
      {footerSlot && state !== "idle" ? createPortal(renderFooterActions(), footerSlot) : null}
    </>
  );

  function renderFileTrigger() {
    return (
      <Button
        type="button"
        color="primary"
        outline
        onClick={handleClickFile}
        disabled={isBusy || !scope}
        data-testid="composer-file-trigger"
        className="w-100 h-100 btn-dropzone"
      >
        <div className="fw-bold text-center" style={{fontSize: "1.5em"}}>
          <FontAwesomeIcon icon="arrow-down" />
        </div>
        <div className="fw-bold text-center">Déposer un fichier</div>
      </Button>
    );
  }

  function renderNoteTrigger() {
    return (
      <Button
        type="button"
        color="primary"
        onClick={handleClickNote}
        disabled={isBusy}
        data-testid="composer-note-trigger"
        className="w-100 h-100"
      >
        <div className="fw-bold text-center" style={{fontSize: "1.5em"}}>
          <FontAwesomeIcon icon="pen" />
        </div>
        <div className="fw-bold text-center">Écrire une note</div>
      </Button>
    );
  }

  function renderNoteMode() {
    return (
      <textarea
        className="form-control"
        autoFocus
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        onKeyDown={handleNoteKeyDown}
        disabled={isBusy}
        placeholder="Votre note…"
        data-testid="composer-note-input"
        style={{flex: 1, resize: "none", minHeight: 0}}
      />
    );
  }

  function renderFileMode() {
    return (
      <>
        <Button
          type="button"
          color="success"
          onClick={handleClickFile}
          disabled={isBusy}
          data-testid="composer-file-trigger"
          className="w-100"
          style={{height: "130px"}}
        >
          <div className="fw-bold text-center" style={{fontSize: "1.5em"}}>
            <FontAwesomeIcon icon="arrow-down" />
          </div>
          <div className="fw-bold text-center">Déposer un fichier</div>
        </Button>
        <div
          className="border border-2 border-dashed rounded my-4 p-3 w-100 h-100 d-flex flex-column"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          data-testid="composer-file-zone"
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-bold">Déposer ou parcourir</span>
            <Button color="primary" outline onClick={openFilePicker} disabled={isBusy}>
              Parcourir
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            disabled={isBusy}
            accept={ACCEPTED_DOCUMENT_MIME_TYPES.join(",")}
            style={{display: "none"}}
            onChange={handleFileInputChange}
          />
          <input
            ref={recoveryInputRef}
            type="file"
            disabled={isBusy}
            accept={ACCEPTED_DOCUMENT_MIME_TYPES.join(",")}
            aria-label="Fichier pour reprendre l'envoi"
            style={{display: "none"}}
            onChange={handleRecoveryFileChange}
          />
          <div className="text-muted small mb-2">
            Après fermeture de la page, les fichiers non reçus doivent être sélectionnés à nouveau.
          </div>
          <div style={{flex: 1, minHeight: 0, overflowY: "auto"}} className="mb-2">
            {pendingFiles.length === 0 ? (
              <div className="text-muted small">Aucun fichier sélectionné</div>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {pendingFiles.map((pending, index) => (
                  <PendingFileTile
                    key={pending.uploadId}
                    pending={pending}
                    disabled={isBusy}
                    onRemove={() => removeFileAt(index)}
                    onReselect={() => handleReselect(pending.uploadId)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  function renderFooterActions() {
    if (state === "note") {
      return (
        <>
          <Button color="secondary" onClick={resetState} disabled={isBusy}>
            Annuler
          </Button>
          <Button
            color="primary"
            onClick={handleSaveNote}
            disabled={isBusy || noteText.trim().length === 0}
            data-testid="composer-note-save"
          >
            {isBusy ? <Spinner size="sm" /> : <FontAwesomeIcon icon="check" />}&nbsp;Enregistrer
          </Button>
        </>
      );
    }
    if (state === "file") {
      return (
        <>
          <Button color="secondary" onClick={resetState} disabled={isBusy}>
            Annuler
          </Button>
          <Button
            color="primary"
            onClick={handleSaveFiles}
            disabled={isBusy || validFiles.length === 0}
            data-testid="composer-file-save"
          >
            {isBusy ? <Spinner size="sm" /> : <FontAwesomeIcon icon="check" />}&nbsp;Enregistrer
          </Button>
        </>
      );
    }
    return null;
  }

  function resetState() {
    pendingIntentIds.current.clear();
    setState("idle");
    setNoteText("");
    setPendingFiles([]);
  }

  function handleClickFile() {
    if (isBusy) {
      return;
    }
    setState("file");
    setTimeout(openFilePicker, 0);
  }

  function handleClickNote() {
    if (isBusy) {
      return;
    }
    setState("note");
  }

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    addFiles(Array.from(e.dataTransfer.files));
  }

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files) {
      return;
    }
    addFiles(Array.from(files));
    e.target.value = "";
  }

  function addFiles(files: File[]) {
    if (isBusy) {
      return;
    }
    const selected: PendingFile[] = files.map((file) => ({
      uploadId: crypto.randomUUID(),
      file,
      filename: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      error: validateDocumentFile(file) ?? undefined,
    }));
    setPendingFiles((prev) => [...prev, ...selected]);
    let nextIndex = 0;
    for (let worker = 0; worker < DOCUMENT_UPLOAD_CONCURRENCY; worker++) {
      void (async () => {
        while (nextIndex < selected.length) {
          const pending = selected[nextIndex++];
          if (pending && !pending.error && pending.file) {
            await handleChecksum(pending, pending.file);
          }
        }
      })();
    }
  }

  async function handleChecksum(pending: PendingFile, file: File) {
    try {
      const checksumSha256 = await documentFileChecksum(file);
      const payload: PrepareDocumentUploadPayload = {
        uploadId: pending.uploadId,
        parentType,
        parentId,
        filename: pending.filename,
        mimeType: pending.mimeType as AcceptedDocumentMimeType,
        sizeBytes: pending.sizeBytes,
        checksumSha256,
      };
      setPendingFiles((current) =>
        current.map((item) =>
          item.uploadId === pending.uploadId ? {...item, payload, status: "waiting"} : item
        )
      );
    } catch {
      setPendingFiles((current) =>
        current.map((item) =>
          item.uploadId === pending.uploadId
            ? {...item, error: "Impossible de vérifier le contenu du fichier"}
            : item
        )
      );
    }
  }

  function handleReselect(uploadId: string) {
    recoveryUploadId.current = uploadId;
    recoveryInputRef.current?.click();
  }

  async function handleRecoveryFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    const pending = pendingFiles.find((item) => item.uploadId === recoveryUploadId.current);
    event.target.value = "";
    if (!file || !pending?.payload || isBusy) {
      return;
    }
    setIsCheckingRecovery(true);
    try {
      if (file.size !== pending.payload.sizeBytes || file.type !== pending.payload.mimeType) {
        toast.error("Ce fichier ne correspond pas à l'envoi initial (taille ou type différent)");
        return;
      }
      const checksum = await documentFileChecksum(file);
      if (checksum !== pending.payload.checksumSha256) {
        toast.error(
          "Ce fichier ne correspond pas à l'envoi initial (contenu, taille ou type différent)"
        );
        return;
      }
      setPendingFiles((current) =>
        current.map((item) =>
          item.uploadId === pending.uploadId
            ? {...item, file, status: "waiting", error: undefined}
            : item
        )
      );
    } catch {
      toast.error("Impossible de vérifier le fichier sélectionné");
    } finally {
      setIsCheckingRecovery(false);
    }
  }

  function removeFileAt(index: number) {
    if (isBusy) {
      return;
    }
    const removed = pendingFiles[index];
    if (removed) {
      pendingIntentIds.current.delete(removed.uploadId);
    }
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function handleNoteKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      resetState();
      return;
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleSaveNote();
    }
  }

  async function handleSaveNote() {
    const body = noteText.trim();
    if (body.length === 0) {
      return;
    }
    try {
      await createTextDocument({parentType, parentId, body});
      toast.success("Note enregistrée", {autoClose: 2200});
      resetState();
    } catch {
      toast.error("Erreur lors de l'enregistrement de la note", {autoClose: 5000});
    }
  }

  function handleUploadState(uploadId: string, status: DocumentUploadState) {
    setPendingFiles((current) =>
      current.map((item) => (item.uploadId === uploadId ? {...item, status} : item))
    );
  }

  async function handleSaveFiles() {
    if (isBusy || validFiles.length === 0) {
      return;
    }
    await handleUploadItems(
      validFiles.flatMap((pending) =>
        pending.payload ? [{payload: pending.payload, file: pending.file}] : []
      ),
      true
    );
  }

  async function handleUploadItems(items: UploadFileDocumentItem[], notify: boolean) {
    if (!scope) {
      return;
    }
    const batchScopeKey = documentUploadManifestKey(scope);
    items.forEach((item) => pendingIntentIds.current.add(item.payload.uploadId));
    try {
      saveDocumentUploadManifest(
        scope,
        items.map((item) => item.payload)
      );
    } catch {
      toast.error(
        "Impossible de mémoriser cet envoi dans le navigateur. Aucun nouvel envoi n'a été lancé."
      );
      return;
    }
    try {
      const {successes, failures} = await uploadFileDocuments(items);
      try {
        completeDocumentUploadManifest(
          scope,
          successes.map((document) => document.id)
        );
      } catch {
        toast.error("Les envois sont vérifiés, mais leur suivi local n'a pas pu être mis à jour");
      }
      if (currentScopeKey.current !== batchScopeKey) {
        return;
      }
      const successfulIds = new Set(successes.map((document) => document.id));
      successfulIds.forEach((uploadId) => pendingIntentIds.current.delete(uploadId));
      setPendingFiles((current) =>
        current.filter((pending) => !successfulIds.has(pending.uploadId))
      );
      if (notify && successes.length > 0) {
        toast.success(
          successes.length === 1 ? "Fichier ajouté" : `${successes.length} fichiers ajoutés`,
          {autoClose: 2200}
        );
      }
      if (failures.length === 0 && pendingIntentIds.current.size === 0) {
        resetState();
      } else if (notify) {
        toast.error("Certains envois restent à reprendre ou à vérifier", {autoClose: 5000});
      }
    } catch {
      toast.error("Erreur lors de la vérification des envois", {autoClose: 5000});
    }
  }
}

function ComposerSlot({
  active,
  collapsed,
  children,
}: {
  active: boolean;
  collapsed: boolean;
  children: React.ReactNode;
}) {
  const flexBasis = active ? "100%" : collapsed ? "0%" : "50%";
  const opacity = collapsed ? 0 : 1;

  return (
    <div
      style={{
        flex: `1 1 ${flexBasis}`,
        minWidth: 0,
        opacity,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        pointerEvents: collapsed ? "none" : "auto",
        transition: "flex-basis 180ms ease-out, opacity 180ms ease-out",
      }}
      aria-hidden={collapsed}
    >
      {children}
    </div>
  );
}

const UPLOAD_STATE_LABELS: Record<DocumentUploadState, string> = {
  waiting: "En attente",
  preparing: "Préparation…",
  uploading: "Envoi…",
  verifying: "Vérification…",
  received: "Reçu",
  failed: "Envoi échoué",
  unknown: "Réception inconnue, à vérifier",
  "missing-file": "Fichier à sélectionner à nouveau",
  invalid: "Contenu reçu non conforme",
};

function pendingFromPayload(payload: PrepareDocumentUploadPayload): PendingFile {
  return {
    uploadId: payload.uploadId,
    filename: payload.filename,
    mimeType: payload.mimeType,
    sizeBytes: payload.sizeBytes,
    payload,
    status: "waiting",
  };
}

function PendingFileTile({
  pending,
  disabled,
  onRemove,
  onReselect,
}: {
  pending: PendingFile;
  disabled: boolean;
  onRemove: () => void;
  onReselect: () => void;
}) {
  const {file} = pending;
  const error =
    pending.error ?? (pending.status === "failed" ? UPLOAD_STATE_LABELS.failed : undefined);
  const isImage = isImageMime(pending.mimeType);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage || !file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setPreviewUrl(reader.result);
      }
    };
    reader.readAsDataURL(file);
    return () => {
      reader.onload = null;
      if (reader.readyState === FileReader.LOADING) {
        reader.abort();
      }
    };
  }, [file, isImage]);

  const badge = getFileTypeBadge(pending.mimeType);
  const subtitle =
    pending.error ??
    (pending.status ? UPLOAD_STATE_LABELS[pending.status] : "Analyse du fichier…");

  return (
    <DocumentCardChrome>
      <DocumentTile
        thumb={
          isImage && file
            ? {kind: "image", src: previewUrl}
            : {kind: "badge", label: badge.label, color: badge.color}
        }
        title={pending.filename}
        subtitle={
          <>
            <span role="status">{subtitle}</span>
            <br />
            {formatBytes(pending.sizeBytes)}
          </>
        }
        subtitleClamp={3}
        error={error}
        reserveFooter={false}
        actions={
          <button
            type="button"
            className="btn btn-sm text-white"
            onClick={onRemove}
            disabled={disabled}
            aria-label={`Retirer ${pending.filename}`}
            style={{
              backgroundColor: "rgba(0,0,0,0.55)",
              width: 22,
              height: 22,
              padding: 0,
              lineHeight: 1,
            }}
          >
            <FontAwesomeIcon icon="times" />
          </button>
        }
      />
      {!file && (
        <Button
          size="sm"
          color="primary"
          outline
          disabled={disabled}
          onClick={onReselect}
          aria-label={`Sélectionner à nouveau ${pending.filename}`}
        >
          Choisir le fichier
        </Button>
      )}
    </DocumentCardChrome>
  );
}
